import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { NgIf } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  InputSignal,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { get, uniqBy } from 'lodash';
import { IDatatableItem } from '../../../pages/items/item-view/items-kanban/items-kanban.interface';
import { EWorkflow, IWorkflowStepBase, TWorkflowStepAssignment } from '../../../pages/items/items.interface';
import { ItemsService } from '../../../pages/items/items.service';
import { ELoadStatus } from '../../../state/state.interface';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import { CountBadgeComponent } from '../count-badge/count-badge.component';
import { ItemCardComponent } from '../item-card/item-card.component';
import { StatusCategoryBadgeComponent } from '../status-category-badge/status-category-badge.component';
import {
  IStepChoiceDialogData,
  StepChoiceDialogComponent,
} from '../step-choice-dialog/step-choice-dialog.component';
import { IKanbanViewStore } from './kanban-view.store';

export interface IKanbanColumnDef {
  id: number | null; // null for the synthetic "Other" column
  title: string;
  workflowStepIds: number[];
}

export interface IRenderColumn<Item> {
  def: IKanbanColumnDef;
  isOther: boolean;
  items: Item[];
}

@Component({
  imports: [
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    CountBadgeComponent,
    TranslatePipe,
    MatIcon,
    NgIf,
    ItemCardComponent,
    FormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    StatusCategoryBadgeComponent,
  ],
  providers: [IKanbanViewStore],
  selector: 'app-kanban-view',
  styleUrl: './kanban-view.component.scss',
  templateUrl: './kanban-view.component.html',
})
export class KanbanViewComponent<Item extends IDatatableItem> {
  public searchTerm = signal<string>('');
  public boardColumnIdPrefix = 'board-column-';
  public items: InputSignal<Item[]> = input.required<Item[]>();
  public columns: InputSignal<IKanbanColumnDef[]> = input.required<IKanbanColumnDef[]>();

  public board: Signal<IRenderColumn<Item>[]> = computed(() => {
    const search = this.searchTerm().toLowerCase();
    const filteredItems = this.items().filter((item) => item.title.toLowerCase().includes(search));

    const stepToColumn = new Map<number, number>();
    this.columns().forEach((column: IKanbanColumnDef, index: number) => {
      for (const stepId of column.workflowStepIds) {
        if (!stepToColumn.has(stepId)) {
          stepToColumn.set(stepId, index);
        }
      }
    });

    const renderColumns: IRenderColumn<Item>[] = this.columns().map((column: IKanbanColumnDef) => ({
      def: column,
      isOther: false,
      items: [],
    }));
    const otherItems: Item[] = [];

    for (const item of filteredItems) {
      const columnIndex: number | undefined = stepToColumn.get(item.currentWorkflowStepId);

      if (columnIndex === undefined) {
        otherItems.push(item);
      } else {
        renderColumns[columnIndex].items.push(item);
      }
    }

    // The synthetic "Other" column only shows up when it actually has items.
    if (otherItems.length) {
      renderColumns.push({
        def: { id: null, title: this.translate.instant('page.items.kanban.otherColumn'), workflowStepIds: [] },
        isOther: true,
        items: otherItems,
      });
    }

    return renderColumns;
  });

  private readonly injector: Injector = inject(Injector);

  constructor(
    public readonly store: IKanbanViewStore,
    private readonly translate: TranslateService,
    private readonly itemsService: ItemsService,
    private readonly matDialog: MatDialog,
    private readonly snackbar: SnackbarService,
  ) {
    this.store.dataStatus$.pipe().subscribe((data: ELoadStatus) => {
      if (data === ELoadStatus.success || data === ELoadStatus.failure) {
        this.itemsService.triggerRefresh();
      }
    });

    // Equalize column heights whenever the rendered board changes — initial load, Kanban View
    // switch, item add/move, or search filter. `afterNextRender` runs the DOM measurement only
    // once the columns have actually been laid out, so no fixed timeout is needed.
    effect(() => {
      this.board();
      afterNextRender(() => KanbanViewComponent.calculateColumnHeight(), { injector: this.injector });
    });
  }

  private static calculateColumnHeight(): void {
    const columns: HTMLElement[] = Array.from(document.getElementsByClassName('scw-kanban-list')) as HTMLElement[];
    const maxHeight: number = Math.max(...columns.map((el: HTMLElement) => el?.offsetHeight));

    columns.forEach((el: HTMLElement): string => (el.style.minHeight = `${maxHeight ?? 500}px`));
  }

  // JIRA-like drag feedback: while dragging, valid target columns are highlighted and invalid ones are blocked.
  public readonly draggingItem: WritableSignal<Item | null> = signal<Item | null>(null);

  public readonly allowedColumns: Signal<boolean[]> = computed(() => {
    const item: Item | null = this.draggingItem();
    const board: IRenderColumn<Item>[] = this.board();

    if (!item) {
      return board.map(() => true);
    }

    return board.map((column: IRenderColumn<Item>) => this.isColumnAllowed(item, column));
  });

  // Per column: the concrete statuses the dragged item can land on there (shown as hints while dragging).
  public readonly columnTargetSteps: Signal<IWorkflowStepBase[][]> = computed(() => {
    const item: Item | null = this.draggingItem();
    const board: IRenderColumn<Item>[] = this.board();

    if (!item) {
      return board.map(() => []);
    }

    return board.map((column: IRenderColumn<Item>) => {
      if (column.isOther) {
        return [];
      }

      // Reorder within the item's own column keeps its current status.
      if (column.def.workflowStepIds.includes(item.currentWorkflowStepId)) {
        return [
          {
            id: item.currentWorkflowStepId,
            isDefault: false,
            name: item.status,
            statusCategory: item.statusCategory as EWorkflow,
          },
        ];
      }

      return this.getNextSteps(item).filter((step: IWorkflowStepBase) =>
        column.def.workflowStepIds.includes(step.id),
      );
    });
  });

  public readonly canEnterColumn = (drag: CdkDrag<Item>, drop: CdkDropList<Item[]>): boolean => {
    const columnIndex: number = Number(drop.id.replace(this.boardColumnIdPrefix, ''));
    const column: IRenderColumn<Item> | undefined = this.board()[columnIndex];

    return column ? this.isColumnAllowed(drag.data, column) : false;
  };

  public onDragStarted(item: Item): void {
    this.draggingItem.set(item);
  }

  public onDragEnded(): void {
    this.draggingItem.set(null);
  }

  private isColumnAllowed(item: Item, column: IRenderColumn<Item>): boolean {
    // The "Other" column only accepts items that already live there (reordering).
    if (column.isOther) {
      return !this.columns().some((definition: IKanbanColumnDef) =>
        definition.workflowStepIds.includes(item.currentWorkflowStepId),
      );
    }

    // The column holding the item's current step is always valid (rank-only move / reorder).
    if (column.def.workflowStepIds.includes(item.currentWorkflowStepId)) {
      return true;
    }

    return this.getNextSteps(item).some((step: IWorkflowStepBase) =>
      column.def.workflowStepIds.includes(step.id),
    );
  }

  public drop(event: CdkDragDrop<Item[]>, targetColumn: IRenderColumn<Item>): void {
    const currentItem: Item = event.item.data;

    // Reordering inside the same column keeps the status and only changes the rank.
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      KanbanViewComponent.calculateColumnHeight();

      this.store.changeRange({
        assignment: {
          itemId: currentItem.id,
          nextItemId: get(event, `container.data.[${event.currentIndex + 1}]`)?.id || null,
          previousItemId: get(event, `container.data.[${event.currentIndex - 1}]`)?.id || null,
          targetWorkflowStepId: currentItem.currentWorkflowStepId,
        },
      });

      return;
    }

    // The "Other" column is not a real column; items cannot be dropped into it.
    if (targetColumn.isOther) {
      return;
    }

    // Moving into the column that already holds the item's current step is a rank-only move.
    if (targetColumn.def.workflowStepIds.includes(currentItem.currentWorkflowStepId)) {
      this.applyTransfer(event, currentItem, currentItem.currentWorkflowStepId);

      return;
    }

    const intersection: IWorkflowStepBase[] = this.getNextSteps(currentItem).filter((step: IWorkflowStepBase) =>
      targetColumn.def.workflowStepIds.includes(step.id),
    );

    if (!intersection.length) {
      this.snackbar.open(this.translate.instant('page.items.kanban.invalidTransition'), ESnackbar.error);

      return;
    }

    if (intersection.length === 1) {
      this.applyTransfer(event, currentItem, intersection[0].id, intersection[0]);

      return;
    }

    // More than one valid next step matches the column: let the user pick.
    const dialogData: IStepChoiceDialogData = {
      itemKey: currentItem.itemKey,
      itemTitle: currentItem.title,
      steps: intersection,
    };

    this.matDialog
      .open(StepChoiceDialogComponent, { data: dialogData, restoreFocus: false, width: '400px' })
      .afterClosed()
      .subscribe((stepId: number | undefined) => {
        if (stepId) {
          this.applyTransfer(
            event,
            currentItem,
            stepId,
            intersection.find((step: IWorkflowStepBase) => step.id === stepId),
          );
        }
      });
  }

  private applyTransfer(
    event: CdkDragDrop<Item[]>,
    currentItem: Item,
    targetWorkflowStepId: number,
    targetStep?: IWorkflowStepBase,
  ): void {
    const previousItem: Item | undefined = get(event, `container.data.[${event.currentIndex - 1}]`);
    const nextItem: Item | undefined = get(event, `container.data.[${event.currentIndex}]`);
    const isStatusChange: boolean = targetWorkflowStepId !== currentItem.currentWorkflowStepId;

    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    currentItem.currentWorkflowStepId = targetWorkflowStepId;
    KanbanViewComponent.calculateColumnHeight();

    this.store.changeRange({
      assignment: {
        itemId: currentItem.id,
        nextItemId: nextItem?.id || null,
        previousItemId: previousItem?.id || null,
        targetWorkflowStepId,
      },
      successMessage:
        isStatusChange && targetStep
          ? this.translate.instant('page.items.statusUpdated', {
              key: currentItem.itemKey,
              status: targetStep.isDefault
                ? this.translate.instant(`system.label.${targetStep.name}`)
                : targetStep.name,
            })
          : undefined,
    });
  }

  private getNextSteps(item: Item): IWorkflowStepBase[] {
    const assignments: TWorkflowStepAssignment[] = item.boardItemConfiguration?.workflow?.workflowStepAssignments ?? [];

    return uniqBy(
      assignments
        .filter(
          (assignment: TWorkflowStepAssignment) =>
            assignment.workflowStep?.id === item.currentWorkflowStepId && assignment.nextWorkflowStep,
        )
        .map((assignment: TWorkflowStepAssignment) => assignment.nextWorkflowStep!),
      'id',
    );
  }

  public openItemEditModal(itemId: number, itemKey: string, boardKey: string): void {
    this.itemsService.openItemEditDialogAndSetUrl(
      boardKey,
      itemId,
      itemKey,
      this.translate.instant('button.saveChanges'),
    );
  }

  public onSearch(event: KeyboardEvent): void {
    this.searchTerm.set(get(event, 'target.value', ''));
  }

  public triggerRefreshData(): void {
    this.itemsService.triggerRefresh();
  }
}
